import { useState } from "react";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Sparkle, Spinner } from "@phosphor-icons/react";

/** AI TL;DR button — generates a 2-line summary of the supplied text via Claude.
 *  Hidden if the source text is under MIN_LEN chars (not worth summarising).
 *  Props: { content, kind?: 'answer'|'question'|'post'|'article' }
 */
const MIN_LEN = 400;

export default function TLDRButton({ content, kind = "answer" }) {
    const { user } = useAuth();
    const [summary, setSummary] = useState("");
    const [loading, setLoading] = useState(false);

    if (!content || content.length < MIN_LEN) return null;

    const generate = async () => {
        if (!user) return toast.error("Sign in to use AI TL;DR");
        if (loading) return;
        setLoading(true);
        try {
            const { data } = await api.post("/ai/summarize", { content, kind });
            setSummary(data.summary || "");
            if (data.rate_remaining != null && data.rate_remaining <= 3) {
                toast.info(`AI quota: ${data.rate_remaining} requests left this hour`);
            }
        } catch (e) {
            toast.error(formatErr(e));
        }
        setLoading(false);
    };

    return (
        <div data-testid={`tldr-${kind}`} className="my-2">
            {summary ? (
                <div className="border border-purple-500/30 bg-purple-500/5 rounded-sm p-3">
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-purple-300 font-mono mb-1.5">
                        <Sparkle size={11} weight="fill" /> AI TL;DR
                    </div>
                    <p className="text-sm text-slate-200 leading-relaxed">{summary}</p>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={generate}
                    disabled={loading}
                    data-testid="tldr-btn"
                    className="text-[10px] uppercase tracking-widest text-purple-300 hover:text-purple-200 border border-purple-500/30 hover:border-purple-500/60 bg-purple-500/5 hover:bg-purple-500/10 px-2.5 py-1 rounded-sm flex items-center gap-1.5 disabled:opacity-50"
                >
                    {loading ? <><Spinner size={11} className="animate-spin" /> summarising…</> : <><Sparkle size={11} weight="fill" /> AI TL;DR</>}
                </button>
            )}
        </div>
    );
}
